import { useProductAccess } from '../../common/hooks/use-product-access';
import useAuth from '../../auth/feature/auth';

/**
 * Slug of the admin-granted Squarebit Framework entitlement. Must match what
 * the admin panel writes into product_access_grants and what the API checks
 * for (SBFRAMEWORK_PRODUCT_SLUG in the backend constants).
 *
 * Note this is NOT the LicenseSpring product code - that is `mix`, and it stays
 * server-side. The two names are deliberately different; see the backend const.
 */
export const SBFRAMEWORK_PRODUCT_SLUG = 'sbframework';

/**
 * Whether this account holds the framework grant.
 *
 * Shares the ['my-access'] query key with My Products, Settings and the other
 * product pages, so however many places ask, it is fetched once - and the
 * realtime access channel invalidates that key, so an admin granting the
 * product opens it in place rather than on the next reload.
 *
 * This decides what to RENDER. It is not the boundary: the package download,
 * the licence key and the page copy are each gated server-side, because the
 * frontend bundle reaches every visitor whatever it chooses to draw.
 */
export const useHasFrameworkAccess = (): {
  granted: boolean;
  isLoading: boolean;
} => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const access = useProductAccess();

  return {
    granted: !!isAuthenticated && access.has(SBFRAMEWORK_PRODUCT_SLUG),
    // Never report "not granted, and settled" while the session is still being
    // restored: on a hard reload isAuthenticated is null for a moment, and a
    // gate that acts on that sends a legitimate holder to the 404.
    isLoading: authLoading || isAuthenticated === null || access.isLoading
  };
};
